/* eslint-disable react/require-default-props */
import React from 'react';
import { Row, Cell } from 'react-table';
import { Box, BoxProps } from '@chakra-ui/core';
import colors from '../../../styles/colors';

type DataType = Record<string, unknown>;

interface TableDataProps<T extends DataType> extends BoxProps {
  cell: Cell<T>;
  children: React.ReactNode;
  borderColor?: string;
  isSelected?: boolean;
}

function TableData<T extends DataType>({ children, cell, borderColor, isSelected, ...rest }: TableDataProps<T>) {
  const tableColor = colors.table;

  return (
    <Box
      {...cell.getCellProps()}
      px={2}
      py={1}
      display="flex"
      alignItems="center"
      sx={{
        height: '100%',
        '&:not(:last-child)': {
          borderRight: '1px solid',
          borderColor: isSelected ? borderColor : tableColor.borderColor,
        },
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

interface TableRowProps<T extends DataType> extends Omit<BoxProps, 'onSelect' | 'onClick'> {
  row: Row<T>;
  getId: (row: T) => React.Key;
  style?: React.CSSProperties;
  isSelected?: boolean;
  onSelect?: (rowData: T) => void;
  renderCell?: (cell: Cell<T>, isSelected: boolean) => React.ReactNode;
}

function TableRowComponent<T extends DataType>({
  row,
  getId,
  style,
  isSelected = false,
  onSelect,
  renderCell,
  ...rest
}: TableRowProps<T>) {
  const tableColor = colors.table;
  return (
    <Box
      {...row.getRowProps({ style })}
      onClick={() => onSelect?.(row.original)}
      display="flex"
      alignItems="center"
      flexDirection="row"
      borderBottom={`1px solid ${tableColor.borderColor}`}
      outline={isSelected ? `1px solid ${tableColor.selectedBorder}` : '1px solid transparent'}
      outlineOffset="-1px"
      bg={isSelected ? tableColor.selectedBg : 'transparent'}
      _hover={{
        bg: isSelected ? tableColor.selectedBg : tableColor.hoverBg,
        cursor: onSelect ? 'pointer' : 'default',
      }}
      data-testid="table-row"
      data-selected={isSelected}
      {...rest}
    >
      {row.cells.map((cell) => (
        <TableData<T> key={getId(cell.row.original)} cell={cell} isSelected={isSelected} borderColor={tableColor.selectedBorder}>
          {renderCell ? renderCell(cell, isSelected) : cell.render('Cell')}
        </TableData>
      ))}
    </Box>
  );
}

export const TableRow = React.memo(TableRowComponent) as <T extends Record<string, unknown>>(
  props: TableRowProps<T>,
) => JSX.Element;
